import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import React from 'react'

interface ConvertToBuilderDialogProps {
  open: boolean
  adAuthorizerName?: string
  pageAuthorizerName?: string
  hasPage: boolean
  converting: boolean
  error?: string
  onConfirm: () => void
  onCancel: () => void
}

const ConvertToBuilderDialog = (props: ConvertToBuilderDialogProps) => {
  const {
    open,
    adAuthorizerName,
    pageAuthorizerName,
    hasPage,
    converting,
    error,
    onConfirm,
    onCancel,
  } = props
  const assetLabel = hasPage ? 'Ad Account and Page' : 'Ad Account'

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Switch to System Users for Marketing Milk?</DialogTitle>
      <DialogContent>
        <p>
          This will attach a <strong>system user</strong> to this business's {assetLabel}, replacing
          the Facebook Authorizer{hasPage ? 's' : ''} currently in use. Marketing Milk will use{' '}
          {hasPage ? 'these system users' : 'this system user'} going forward instead of the
          authorizer{hasPage ? 's' : ''} below.
        </p>
        <div className="border rounded p-3 mb-3">
          <Typography variant="overline" color="textSecondary" style={{ lineHeight: 1.2 }}>
            Currently configured authorizer{hasPage ? 's' : ''}
          </Typography>
          <div className="d-flex" style={{ gap: 24 }}>
            <div>
              <Typography variant="body2" color="textSecondary">
                Ad Account Authorizer
              </Typography>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                {adAuthorizerName ?? 'Not set'}
              </Typography>
            </div>
            {hasPage && (
              <div>
                <Typography variant="body2" color="textSecondary">
                  Page Authorizer
                </Typography>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  {pageAuthorizerName ?? 'Not set'}
                </Typography>
              </div>
            )}
          </div>
        </div>
        <p>
          <strong>Note this pairing down before continuing.</strong> A successful switch clears the
          authorizer{hasPage ? 's' : ''} above from this business entirely — reverting later turns
          Facebook Builder back off, but you'll need to reselect the authorizer{hasPage ? 's' : ''}{' '}
          yourself.
        </p>
        <p>
          The initial switch depends on the permissions the authorizer above currently has on the{' '}
          {assetLabel}. Going forward, any changes to these connections will depend on{' '}
          <strong>your own</strong> Facebook permissions, not the authorizer's.
        </p>
        <Alert severity="info" className="mt-2">
          This feature is in beta.
        </Alert>
        {converting && (
          <div className="d-flex align-items-center mt-3" style={{ gap: 12 }}>
            <CircularProgress size={20} thickness={5} />
            <Typography variant="body2" color="textSecondary">
              Attaching a system user to the {assetLabel} — this can take a few seconds...
            </Typography>
          </div>
        )}
        {error && (
          <Alert severity="error" className="mt-2">
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={converting}>
          Cancel
        </Button>
        <Button onClick={onConfirm} color="primary" variant="contained" disabled={converting}>
          {converting ? (
            <>
              <CircularProgress size={16} thickness={5} color="inherit" className="mr-2" />
              Converting...
            </>
          ) : (
            'Attach System User'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConvertToBuilderDialog
